import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { OrderSchemaType, ItemPath } from '../order.schema';
import { OrderItemType } from '../types';

interface useItemSubtotalCalculatorProps {
  itemPath: ItemPath;
  itemType: OrderItemType;
  quantity: number;
}

interface ItemSubtotalCalculatorResult {
  unitPrice: number;
  itemTaxes: number;
  itemSubtotal: number;
}

export const useItemSubtotalCalculator = ({ itemPath, itemType, quantity }: useItemSubtotalCalculatorProps): ItemSubtotalCalculatorResult => {
  const { watch, setValue, control } = useFormContext<OrderSchemaType>();

  const priceDetailsPath = `${itemPath}.priceDetails` as const;
  const offeredProductMaterialPath = `${itemPath}.offeredProductMaterial` as const;

  const [weight, pricePerKgFinal, unitPrice, itemSubtotal, itemTaxes] = watch([
    `${offeredProductMaterialPath}.weigth`,
    `${priceDetailsPath}.pricePerKgFinal`,
    `${priceDetailsPath}.unitPrice`,
    `${priceDetailsPath}.itemSubtotal`,
    `${priceDetailsPath}.itemTaxes`
  ]);

  // Item SERVICE precificado por custo usa item.itemPrice (fórmula custo × qtd × markup,
  // escrita pelo ServicesSection).
  const serviceCost = watch(`${itemPath}.cost` as any) as number | undefined;
  const servicePrice = watch(`${itemPath}.itemPrice` as any) as number | undefined;
  const itemMarkup = watch(`${itemPath}.markup` as any) as number | undefined;

  const subItems = useWatch({
    control,
    name: `${itemPath}.subItems` as any,
  });

  useEffect(() => {
    const numQty = Number(quantity) || 0;
    const numWeight = parseFloat(String(weight) || '0');
    const numFinalPriceKg = pricePerKgFinal ?? 0;
    const numUnitPrice = unitPrice ?? 0;
    const isServiceCostPriced = itemType === OrderItemType.SERVICE && (Number(serviceCost) || 0) > 0;
    let calculatedSubtotal = 0;

    if (isServiceCostPriced) {
      // Serviço com custo cadastrado: preço já calculado/persistido em item.itemPrice
      calculatedSubtotal = Number(servicePrice) || 0;
    } else if (itemType === OrderItemType.PRODUCT) {
      let sum = 0;
      if (subItems && Array.isArray(subItems)) {
        (subItems as any[]).forEach((subItem: any) => {
          const subTotal = Number(subItem.priceDetails?.itemSubtotal)
            || Number(subItem.itemPrice)
            || (Number(subItem.priceDetails?.unitPrice) * Number(subItem.feedstock?.quantidade))
            || 0;
          sum += subTotal;
        });
      }
      const markupFactor = (Number(itemMarkup) || 0) > 0 ? Number(itemMarkup) : 1;
      calculatedSubtotal = sum * markupFactor;
    } else if (itemType === OrderItemType.MATERIAL) {
      if (numQty > 0) {
        if (numUnitPrice > 0) {
          calculatedSubtotal = numUnitPrice * numQty;
        } else if (numFinalPriceKg > 0 && numWeight > 0) {
          // Fallback legado (pedidos antigos por kg).
          calculatedSubtotal = numFinalPriceKg * numWeight * numQty;
        }
      }
    } else if (itemType) {
      if (numQty > 0 && numUnitPrice > 0) {
        calculatedSubtotal = numUnitPrice * numQty;
      }
    }

    // Serviço agregado a item não-PE (ex: beneficiamento sobre MP) continua somando.
    if (itemType !== OrderItemType.PRODUCT && subItems && Array.isArray(subItems)) {
      (subItems as any[]).forEach((subItem: any) => {
        if (subItem.itemType === 'SERVICE') {
          const serviceTotal = Number(subItem.priceDetails?.itemSubtotal) || Number(subItem.itemPrice) || 0;
          calculatedSubtotal += serviceTotal;
        }
      });
    }

    const roundedSubtotal = parseFloat(calculatedSubtotal.toFixed(2));
    const currentSubtotalState = watch(`${priceDetailsPath}.itemSubtotal`);

    if (currentSubtotalState !== roundedSubtotal) {
      setValue(`${priceDetailsPath}.itemSubtotal`, roundedSubtotal, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    // Persiste o resultado em item.itemPrice (vai pro backend); servico precificado
    // por custo nao e sobrescrito - la o itemPrice e a fonte.
    if (!isServiceCostPriced) {
      const currentItemPrice = watch(`${itemPath}.itemPrice` as any);
      if (currentItemPrice !== roundedSubtotal) {
        setValue(`${itemPath}.itemPrice` as any, roundedSubtotal, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    }
  }, [itemType, quantity, weight, pricePerKgFinal, unitPrice, serviceCost, servicePrice, itemMarkup, subItems, setValue, itemPath, priceDetailsPath, watch]);
  
  return {
    unitPrice: unitPrice ?? 0,
    itemTaxes: itemTaxes ?? 0,
    itemSubtotal: itemSubtotal ?? 0,
  };
};